import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi, urlApi } from './api';
import { envoyerParMorceaux } from './depot-par-morceaux';
import type { ReleveAffiche } from './useEmargement';

/**
 * Classes à distance, replays et plan de formation (CDC §7.2).
 *
 * Les états, les refus et les droits viennent de l'API (`domain/classe-distance.ts`
 * côté serveur) : chaque ligne porte ce qui empêche chaque geste, l'écran ne
 * recalcule rien.
 */

export type DemiJournee = 'MATIN' | 'APRES_MIDI';
export type EtatClasse = 'PROGRAMMEE' | 'EN_DIRECT' | 'TERMINEE' | 'NON_TENUE';
export type OrigineReplay = 'ENREGISTREMENT' | 'DEPOT' | 'LIEN';
export type StatutReplay = 'EN_COURS' | 'DISPONIBLE' | 'ECHEC' | 'RETIRE';

export interface EtatDiffusion {
  raccordee: boolean;
  enregistrement: boolean;
  motif: string | null;
}

export interface LigneClasse {
  seanceId: string;
  libelle: string;
  date: string;
  demiJournee: DemiJournee;
  heureDebut: string;
  heureFin: string;
  siteCode: string;
  promotion: { cle: string; libelle: string };
  bloc: { id: string; libelle: string } | null;
  formateurId: string | null;
  formateur: string | null;
  etat: EtatClasse;
  etatLibelle: string;
  etatTon: string;
  attendus: number;
  emarges: number;
  replays: number;
  enregistrementEnCours: boolean;
}

export interface LigneReplay {
  id: string;
  seanceId: string;
  titre: string;
  origine: OrigineReplay;
  origineLibelle: string;
  statut: StatutReplay;
  statutLibelle: string;
  statutTon: string;
  creeLe: string;
  disponibleLe: string | null;
  dureeSecondes: number | null;
  tailleOctets: number | null;
  tailleAttendue: number | null;
  recus: number | null;
  url: string | null;
  lecture: boolean;
  vues: number;
  creePar: string | null;
  motif: string | null;
  retireLe: string | null;
  retirePar: string | null;
  conserveJusquau: string | null;
  refusRetrait: string | null;
}

export interface LignePlan {
  blocId: string | null;
  libelle: string;
  heuresProgramme: number | null;
  classesTenues: number;
  classesAVenir: number;
  heuresTenues: number;
  heuresAVenir: number;
  replays: number;
}

export interface PlanFormation<L extends LignePlan = LignePlan> {
  lignes: L[];
  heuresProgramme: number;
  heuresDistancePrevues: number;
  heuresDistanceTenues: number;
  heuresDistanceAVenir: number;
}

export interface DetailClasse extends LigneClasse {
  formation: { code: string; titre: string } | null;
  blocs: { id: string; libelle: string }[];
  /** Émargement numérique : chaque attendu, son émargement et le relevé de la salle. */
  participants: {
    presenceId: string;
    apprenantId: string;
    nom: string;
    statut: 'ATTENDU' | 'PRESENT' | 'RETARD' | 'ABSENT';
    emarge: boolean;
    emargeLe: string | null;
    salle: ReleveAffiche;
    refusRelance: string | null;
  }[];
  diffusion: { ouverteLe: string | null; fermeeLe: string | null; ouvertePar: string | null };
  feuille: { formateurSigneLe: string | null; clotureeLe: string | null; documentId: string | null };
  replaysDetail: LigneReplay[];
  plan: PlanFormation;
  estAnimateur: boolean;
  refus: {
    ouverture: string | null;
    fermeture: string | null;
    rejoindre: string | null;
    enregistrement: string | null;
    arretEnregistrement: string | null;
    retrait: string | null;
    modificationHoraires: string | null;
    depot: string | null;
    attestation: string | null;
    cloture: string | null;
  };
}

export interface PromotionProgrammable {
  cle: string;
  libelle: string;
  siteCode: string;
  formation: { code: string; titre: string };
  effectif: number;
  blocs: { id: string; libelle: string; heures: number }[];
  horaires: Record<DemiJournee, { debut: string; fin: string }>;
}

export interface FormateurProposable {
  id: string;
  nom: string;
  sites: string[];
}

export interface VueSalle {
  diffusion: EtatDiffusion;
  estFormateur: boolean;
  refusProgrammation: string | null;
  classes: LigneClasse[];
  selection: DetailClasse | null;
  promotions: PromotionProgrammable[];
  formateurs: FormateurProposable[];
}

export interface AccesSalle {
  url: string;
  jeton: string;
  salle: string;
}

export function useSalleElearning(site: string, seanceId: string | null) {
  const parametres = new URLSearchParams();
  if (site && site !== 'TOUS') parametres.set('site', site);
  if (seanceId) parametres.set('seanceId', seanceId);
  const requete = parametres.toString();
  return useQuery({
    queryKey: ['salle-elearning', requete],
    queryFn: () => appelApi<VueSalle>(`/elearning/salle${requete ? `?${requete}` : ''}`),
    // Un enregistrement se termine côté serveur : la salle se relit d'elle-même.
    refetchInterval: (requeteCourante) =>
      requeteCourante.state.data?.selection?.etat === 'EN_DIRECT' ||
      requeteCourante.state.data?.selection?.replaysDetail.some((replay) => replay.statut === 'EN_COURS')
        ? 15_000
        : false,
  });
}

export interface EntreeProgrammation {
  promotion: string;
  date: string;
  demiJournee: DemiJournee;
  heureDebut: string;
  heureFin: string;
  libelle?: string;
  moduleFormationId?: string | null;
  formateurId?: string | null;
}

export type ModificationClasse = Partial<Omit<EntreeProgrammation, 'promotion'>>;

/** Gestes de la salle : chacun relit la salle, qui porte les nouveaux refus. */
export function useGestesClasse() {
  const client = useQueryClient();
  const relire = () => client.invalidateQueries({ queryKey: ['salle-elearning'] });
  // La feuille d'émargement et le suivi des absences lisent aussi ce que la
  // salle atteste et clôture.
  const relireFeuille = () =>
    Promise.all([
      relire(),
      client.invalidateQueries({ queryKey: ['emargement'] }),
      client.invalidateQueries({ queryKey: ['absences'] }),
    ]);
  const geste = <E, R>(appel: (entree: E) => Promise<R>, apres: () => Promise<unknown> = relire) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMutation({ mutationFn: appel, onSuccess: apres });

  return {
    programmer: geste((entree: EntreeProgrammation) =>
      appelApi<{ classe: DetailClasse }>('/elearning/classes', { methode: 'POST', corps: entree }),
    ),
    modifier: geste(({ seanceId, saisie }: { seanceId: string; saisie: ModificationClasse }) =>
      appelApi(`/elearning/classes/${seanceId}`, { methode: 'PATCH', corps: saisie }),
    ),
    retirer: geste((seanceId: string) => appelApi(`/elearning/classes/${seanceId}`, { methode: 'DELETE' })),
    ouvrir: geste((seanceId: string) =>
      appelApi(`/elearning/classes/${seanceId}/ouverture`, { methode: 'POST' }),
    ),
    fermer: geste((seanceId: string) =>
      appelApi(`/elearning/classes/${seanceId}/fermeture`, { methode: 'POST' }),
    ),
    demarrerEnregistrement: geste((seanceId: string) =>
      appelApi(`/elearning/classes/${seanceId}/enregistrement`, { methode: 'POST' }),
    ),
    arreterEnregistrement: geste((seanceId: string) =>
      appelApi(`/elearning/classes/${seanceId}/enregistrement`, { methode: 'DELETE' }),
    ),
    relancer: geste(({ seanceId, apprenantId }: { seanceId: string; apprenantId: string }) =>
      appelApi(`/elearning/classes/${seanceId}/participants/${apprenantId}/relance`, { methode: 'POST' }),
    ),
    // Attestation et clôture sont celles de la feuille d'émargement : mêmes
    // routes que la tablette, la salle se relit ensuite.
    attester: geste(
      (seanceId: string) => appelApi(`/emargement/seances/${seanceId}/signature-formateur`, { methode: 'POST' }),
      relireFeuille,
    ),
    cloturer: geste(
      (seanceId: string) =>
        appelApi<{ message: string }>(`/emargement/seances/${seanceId}/cloture`, { methode: 'POST' }),
      relireFeuille,
    ),
    lierReplay: geste(({ seanceId, titre, url }: { seanceId: string; titre: string; url: string }) =>
      appelApi(`/elearning/classes/${seanceId}/replays`, {
        methode: 'POST',
        corps: { origine: 'LIEN', titre, url },
      }),
    ),
    retirerReplay: geste(({ replayId, motif }: { replayId: string; motif?: string }) =>
      appelApi(`/elearning/replays/${replayId}`, { methode: 'DELETE', corps: motif ? { motif } : {} }),
    ),
    relire,
  };
}

export function jetonAnimateur(seanceId: string): Promise<AccesSalle> {
  return appelApi<AccesSalle>(`/elearning/classes/${seanceId}/jeton`, { methode: 'POST' });
}

export function urlVideoReplay(replayId: string, espace: 'equipe' | 'apprenant'): string {
  return urlApi(
    espace === 'apprenant'
      ? `/espace-apprenant/elearning/replays/${replayId}/video`
      : `/elearning/replays/${replayId}/video`,
  );
}

// ---------------------------------------------------------------------------
// Dépôt d'un replay par morceaux
// ---------------------------------------------------------------------------

export async function envoyerFichierReplay(
  replayId: string,
  fichier: File,
  onProgression: (recus: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const base = `/elearning/replays/${replayId}`;
  await envoyerParMorceaux(
    {
      progression: `${base}/progression`,
      morceau: (debut) => `${base}/morceaux?debut=${debut}`,
      finalisation: `${base}/finalisation`,
    },
    fichier,
    onProgression,
    signal,
  );
}

export async function annoncerDepotReplay(
  seanceId: string,
  titre: string,
  fichier: File,
): Promise<string> {
  const { replay } = await appelApi<{ replay: { id: string } }>(`/elearning/classes/${seanceId}/replays`, {
    methode: 'POST',
    corps: {
      origine: 'DEPOT',
      titre,
      nomFichier: fichier.name,
      typeMime: fichier.type,
      tailleOctets: fichier.size,
    },
  });
  return replay.id;
}

// ---------------------------------------------------------------------------
// Espace apprenant
// ---------------------------------------------------------------------------

export interface ClasseApprenant {
  seanceId: string;
  libelle: string;
  date: string;
  demiJournee: DemiJournee;
  heureDebut: string;
  heureFin: string;
  bloc: string | null;
  formateur: string | null;
  etat: EtatClasse;
  etatLibelle: string;
  etatTon: string;
  refusRejoindre: string | null;
  /**
   * Émargement d'un geste dans la salle. Le refus ne dit que ce qui se sait
   * sans la salle ; la caméra est vérifiée par l'API au moment du geste.
   */
  emargement: { emargeLe: string | null; refus: string | null };
}

export interface ReplayApprenant {
  id: string;
  titre: string;
  seanceId: string;
  classe: string;
  date: string;
  blocId: string | null;
  dureeSecondes: number | null;
  lecture: boolean;
  url: string | null;
  vu: boolean;
}

export interface VueElearningApprenant {
  diffusion: EtatDiffusion;
  formation: { titre: string } | null;
  enDirect: ClasseApprenant | null;
  classes: ClasseApprenant[];
  plan: PlanFormation<LignePlan & { listeReplays: ReplayApprenant[] }>;
  replays: ReplayApprenant[];
}

export function useElearningApprenant() {
  return useQuery({
    queryKey: ['elearning-apprenant'],
    queryFn: () => appelApi<VueElearningApprenant>('/espace-apprenant/elearning'),
    // La salle s'ouvre quand le formateur lance la diffusion : l'apprenant
    // qui attend sur la page voit le bouton apparaître sans recharger.
    refetchInterval: 30_000,
  });
}

export function jetonApprenant(seanceId: string): Promise<AccesSalle> {
  return appelApi<AccesSalle>(`/espace-apprenant/elearning/classes/${seanceId}/jeton`, { methode: 'POST' });
}

export interface ResultatEmargementDistance {
  emargeLe: string;
  statut: 'PRESENT' | 'RETARD';
  heureArrivee: string | null;
  mention: string;
}

/** « J'émarge » : accepté seulement si l'API voit l'apprenant dans la salle, caméra allumée. */
export function useEmargerADistance() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (seanceId: string) =>
      appelApi<ResultatEmargementDistance>(`/espace-apprenant/elearning/classes/${seanceId}/emargement`, {
        methode: 'POST',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['elearning-apprenant'] });
      void client.invalidateQueries({ queryKey: ['espace-apprenant'] });
      void client.invalidateQueries({ queryKey: ['emargement'] });
    },
  });
}

/** Heure `HH:MM` de La Réunion d'un horodatage de l'API. */
export function heureReunion(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Mise en forme
// ---------------------------------------------------------------------------

export function dateCourte(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

export function dureeLisible(secondes: number | null): string | null {
  if (!secondes) return null;
  const heures = Math.floor(secondes / 3600);
  const minutes = Math.round((secondes % 3600) / 60);
  if (heures === 0 && minutes === 0) return 'moins d’1 min';
  return heures > 0 ? `${heures} h ${String(minutes).padStart(2, '0')}` : `${minutes} min`;
}

export function tailleLisible(octets: number | null): string | null {
  if (!octets) return null;
  if (octets >= 1024 ** 3) return `${(octets / 1024 ** 3).toFixed(1).replace('.', ',')} Go`;
  if (octets < 1024 ** 2) return 'moins d’1 Mo';
  return `${Math.round(octets / 1024 ** 2)} Mo`;
}

export function heuresLisibles(heures: number): string {
  return `${Number.isInteger(heures) ? heures : heures.toFixed(1).replace('.', ',')} h`;
}

export const LIBELLES_DEMI_JOURNEE: Record<DemiJournee, string> = {
  MATIN: 'Matin',
  APRES_MIDI: 'Après-midi',
};
